// Agregaciones del cuadro de mando (spec cuadro-mando §4). Puras: operan
// sobre LlamadaFeedRow[] ya cargado; cero IO, cero React. Clasifican con
// estadoVisual (misma semántica que el feed de Atención).

import { diaMadrid } from '../llamadas/fechas';
import {
  destinoEscalado,
  estadoVisual,
  etiquetaMotivo,
  type DestinoEscalado,
} from '../llamadas/presentacion';
import type { LlamadaFeedRow } from '../../types/llamadas';
import { SIN_CLIENTE, etiquetaCliente } from './filtros';

const pctSobre = (valor: number, total: number): number =>
  total > 0 ? Math.round((valor / total) * 100) : 0;

// --- Serie diaria (gráfica de barras) ---

export interface PuntoSerieDiaria {
  dia: string; // YYYY-MM-DD
  total: number;
  resueltas: number;
  escaladas: number;
}

// Un punto por cada día de `dias` (los vacíos quedan a cero), mismo orden.
// Cada llamada se asigna a su día con diaMadrid(inicio); fuera de `dias` se
// ignora (defensivo: el fetch ya acota el rango).
export function serieDiaria(llamadas: LlamadaFeedRow[], dias: string[]): PuntoSerieDiaria[] {
  const porDia = new Map<string, PuntoSerieDiaria>(
    dias.map((dia) => [dia, { dia, total: 0, resueltas: 0, escaladas: 0 }])
  );
  for (const l of llamadas) {
    if (l.inicio === null) continue;
    const punto = porDia.get(diaMadrid(l.inicio));
    if (!punto) continue;
    punto.total++;
    const estado = estadoVisual(l);
    if (estado === 'resuelta') punto.resueltas++;
    if (estado === 'escalada') punto.escaladas++;
  }
  return dias.map((dia) => porDia.get(dia) as PuntoSerieDiaria);
}

const MES_CORTO = new Intl.DateTimeFormat('es-ES', { month: 'short', timeZone: 'UTC' });
const DIA_SEMANA = new Intl.DateTimeFormat('es-ES', { weekday: 'short', timeZone: 'UTC' });

function nombreMes(dia: string): string {
  return MES_CORTO.format(new Date(`${dia}T12:00:00Z`)).replace('.', '');
}

// Cuántos días caben legiblemente en el eje. Antes esto se deducía del preset
// (`periodo === '30d'`), pero el preset era un proxy: lo que importa es el número
// de días, y un rango custom no tiene preset ninguno.
export type DensidadEjeX = 'dia-semana' | 'dia-mes' | 'dia-y-mes';

// Etiqueta del eje x: 'dia-semana' → día de semana corto es-ES a 2 letras;
// 'dia-mes' → día del mes sin cero inicial; 'dia-y-mes' → los dos.
export function etiquetaEjeX(dia: string, densidad: DensidadEjeX): string {
  if (densidad === 'dia-y-mes') return `${Number(dia.slice(8))} ${nombreMes(dia)}`;
  if (densidad === 'dia-mes') return String(Number(dia.slice(8)));
  return DIA_SEMANA.format(new Date(`${dia}T12:00:00Z`)).slice(0, 2);
}

// --- Agrupación de la serie (change 002, decisión C1) ---
//
// El rango custom rompió el supuesto de la gráfica de barras: una barra por día
// solo se lee mientras los días caben. A 366 días cada barra mide 0,84 px — un
// peine ilegible. La primera respuesta fue un tope de un año, pero el tope no
// arreglaba nada: dejaba fuera los rangos largos Y seguía dibujando el peine
// justo por debajo del límite. Así que se agrupa.

export type Granularidad = 'dia' | 'semana' | 'mes';

// Umbrales de LEGIBILIDAD, no de coste: el coste lo guarda el umbral de filas.
// El criterio es cuántas barras caben en 600 px de viewBox con una anchura que
// se pueda mirar: 90 barras salen a 3,7 px, 53 semanas a 6,3 px y 36 meses a
// 9,3 px. Por debajo de ~3 px la barra deja de ser una barra.
export function granularidadPara(nDias: number): Granularidad {
  if (nDias <= 90) return 'dia';
  if (nDias <= 366) return 'semana';
  return 'mes';
}

export interface PuntoSerie {
  clave: string; // primer día del cubo (YYYY-MM-DD): key de React y orden
  etiqueta: string; // lo que se pinta en el eje x
  rotulo: string; // lo que se dice en el tooltip
  dias: number; // días naturales agrupados — un cubo parcial se ve aquí
  total: number;
  resueltas: number;
  escaladas: number;
}

// Un cubo puede ser parcial (un rango que empieza en miércoles) y eso hace que
// una semana corta parezca un bajón. El tooltip dice el tramo real que agrupa,
// que es la forma barata de que no engañe.
function textoTramo(desde: string, hasta: string): string {
  const [aD, , dD] = desde.split('-');
  const [aH, , dH] = hasta.split('-');
  if (desde === hasta) return `${Number(dD)} ${nombreMes(desde)} ${aD}`;
  if (desde.slice(0, 7) === hasta.slice(0, 7))
    return `${Number(dD)}–${Number(dH)} ${nombreMes(desde)} ${aD}`;
  const inicio = `${Number(dD)} ${nombreMes(desde)}${aD === aH ? '' : ` ${aD}`}`;
  return `${inicio} – ${Number(dH)} ${nombreMes(hasta)} ${aH}`;
}

// Lunes de la semana de `dia` (ISO: la semana empieza en lunes).
function lunesDe(dia: string): string {
  const t = Date.parse(`${dia}T00:00:00Z`);
  const diaSemana = (new Date(t).getUTCDay() + 6) % 7; // 0 = lunes
  return new Date(t - diaSemana * 86_400_000).toISOString().slice(0, 10);
}

// Agrupa la serie diaria en cubos de la granularidad pedida, conservando el
// orden. Con 'dia' es la identidad salvo por las etiquetas, que se calculan
// aquí para que la gráfica no tenga que decidir nada.
export function agruparSerie(serie: PuntoSerieDiaria[], g: Granularidad): PuntoSerie[] {
  if (g === 'dia') {
    // Con más de diez días el día de la semana se repite y deja de informar,
    // así que se pasa al día del mes. Y si además el rango cruza de mes, el día
    // suelto TAMPOCO informa: un eje de 76 días pintaba «2 10 18 26 4 12 20 28
    // 5 13» y no había manera de saber si ese 4 era de junio o de julio. Visto
    // en producción el 16-ago, con el rango custom, que es lo que lo destapa:
    // ningún preset llega a repetir número de día. Caben de sobra — solo se
    // dibuja una etiqueta de cada `ceil(n/10)`, o sea unas diez en total.
    const cruzaMeses =
      serie.length > 0 && serie[0]!.dia.slice(0, 7) !== serie.at(-1)!.dia.slice(0, 7);
    const densidad: DensidadEjeX =
      serie.length <= 10 ? 'dia-semana' : cruzaMeses ? 'dia-y-mes' : 'dia-mes';
    return serie.map((p) => ({
      clave: p.dia,
      etiqueta: etiquetaEjeX(p.dia, densidad),
      rotulo: p.dia,
      dias: 1,
      total: p.total,
      resueltas: p.resueltas,
      escaladas: p.escaladas,
    }));
  }

  const clavePara = (dia: string) => (g === 'semana' ? lunesDe(dia) : `${dia.slice(0, 7)}-01`);
  const cubos = new Map<string, PuntoSerie & { desde: string; hasta: string }>();

  for (const p of serie) {
    const clave = clavePara(p.dia);
    const cubo = cubos.get(clave);
    if (!cubo) {
      cubos.set(clave, {
        clave,
        etiqueta: '',
        rotulo: '',
        dias: 1,
        desde: p.dia,
        hasta: p.dia,
        total: p.total,
        resueltas: p.resueltas,
        escaladas: p.escaladas,
      });
      continue;
    }
    cubo.dias++;
    cubo.hasta = p.dia;
    cubo.total += p.total;
    cubo.resueltas += p.resueltas;
    cubo.escaladas += p.escaladas;
  }

  const lista = [...cubos.values()];
  // El año en el eje solo cuando el periodo cruza de año: si no, es ruido
  // repetido en cada etiqueta.
  const cruzaAnios =
    lista.length > 0 && lista[0]!.desde.slice(0, 4) !== lista.at(-1)!.hasta.slice(0, 4);

  return lista.map(({ desde, hasta, ...cubo }) => ({
    ...cubo,
    rotulo: textoTramo(desde, hasta),
    etiqueta:
      g === 'semana'
        ? `${Number(desde.slice(8))} ${nombreMes(desde)}`
        : `${nombreMes(desde)}${cruzaAnios ? ` ${desde.slice(2, 4)}` : ''}`,
  }));
}

// --- Desenlace (donut) ---

export type ClaveDesenlace =
  | 'resueltas'
  | 'esc_operaciones'
  | 'esc_comercial'
  | 'esc_otras'
  | 'sin_analizar';

export interface SegmentoDesenlace {
  clave: ClaveDesenlace;
  etiqueta: string;
  valor: number;
  pct: number;
}

export interface Desenlace {
  total: number; // centro del donut
  segmentos: SegmentoDesenlace[];
}

const ETIQUETA_DESENLACE: Record<ClaveDesenlace, string> = {
  resueltas: 'Resueltas IA',
  esc_operaciones: 'Esc. a operaciones',
  esc_comercial: 'Esc. a comercial',
  esc_otras: 'Esc. otras',
  sin_analizar: 'En curso / procesando',
};

// Devuelve SIEMPRE los 5 segmentos en este orden aunque valgan 0 (el
// componente omite los arcos a 0). pct sobre total; total=0 → pct 0.
export function desenlace(llamadas: LlamadaFeedRow[]): Desenlace {
  const contador: Record<ClaveDesenlace, number> = {
    resueltas: 0,
    esc_operaciones: 0,
    esc_comercial: 0,
    esc_otras: 0,
    sin_analizar: 0,
  };
  for (const l of llamadas) {
    const estado = estadoVisual(l);
    if (estado === 'resuelta') contador.resueltas++;
    else if (estado === 'escalada') {
      if (l.motivo === 'operacion') contador.esc_operaciones++;
      else if (l.motivo === 'comercial') contador.esc_comercial++;
      else contador.esc_otras++;
    } else contador.sin_analizar++;
  }
  const total = llamadas.length;
  const claves: ClaveDesenlace[] = [
    'resueltas',
    'esc_operaciones',
    'esc_comercial',
    'esc_otras',
    'sin_analizar',
  ];
  return {
    total,
    segmentos: claves.map((clave) => ({
      clave,
      etiqueta: ETIQUETA_DESENLACE[clave],
      valor: contador[clave],
      pct: pctSobre(contador[clave], total),
    })),
  };
}

// --- Distribución de motivos ---

export interface MotivoAgregado {
  motivo: string; // valor crudo o 'sin_motivo'
  etiqueta: string;
  total: number;
  pct: number;
}

// Solo motivos presentes, orden total DESC (empate: alfabético de etiqueta).
export function distribucionMotivos(llamadas: LlamadaFeedRow[]): MotivoAgregado[] {
  const conteo = new Map<string, number>();
  for (const l of llamadas) {
    // Veracidad y no nulidad: un motivo de cadena vacía es «sin motivo» en la tabla,
    // en el Excel, en los chips y en la cabecera impresa. Agrupándolo por nulidad se
    // quedaba en su propio cubo, con etiqueta vacía, y el panel pintaba dos entradas
    // para lo mismo —una barra sin nombre— también en el PDF (vuelta 6 del gate).
    //
    // Esto es CINTURÓN, y su LÍMITE es exactamente esos dos casos: la ausencia y la
    // cadena vacía. Un motivo visualmente vacío (`'  '`, `'\u200B'`) abre su propio cubo
    // con etiqueta invisible, y uno con invisibles delante (`'\u200Bcomercial'`) parte un
    // motivo válido en dos barras que se leen igual — las dos cosas, también en el PDF.
    // Quien las cierra es la frontera del dato al escribir (el `asStr` del webhook de
    // Retell, único escritor de `llamadas.motivo` en producción), que viaja en el cambio
    // `registro-llamadas` y AQUÍ NO ESTÁ: en este árbol las pantallas se defienden solas,
    // con el dato tal cual está hoy en la base. No se sanea en el frontend a propósito:
    // hay cinco caminos de lectura en `services/llamadas.ts` y DIEZ puntos de pantalla
    // que contestan «¿hay motivo?» — enumerados uno a uno en el límite de `etiquetaMotivo`.
    //
    // Y un segundo límite, del CENTINELA: `'sin_motivo'` es una clave literal y también
    // un valor legal de la columna, que es `text` sin CHECK y que nada valida —
    // comprobado contra `upsert_llamada` en el stack local, que corre esta misma
    // migración: `'sin_motivo'` se acepta y se guarda tal cual. Si el análisis
    // emitiera ese literal (está fuera de su taxonomía declarada —`rechazable`,
    // `administrativo`, `comercial`, `operacion`, `otro`—, así que hace falta que se
    // desvíe), su cubo y el de los vacíos serían el mismo: con 3 llamadas de
    // `'sin_motivo'`, 2 de `null` y 1 de `''`, el panel «Motivos» pinta una barra «Sin
    // clasificar» = 6 y el chip «Sin clasificar» deja la tabla en 3 filas. Misma
    // etiqueta, dos conjuntos. NO se arregla aquí: separar la clave produce dos barras
    // con el mismo nombre, que es peor, y decidir cuál de las dos es «Sin clasificar» —o
    // cómo se llama la otra— es diseño, no un arreglo.
    const motivo = l.motivo || 'sin_motivo';
    conteo.set(motivo, (conteo.get(motivo) ?? 0) + 1);
  }
  return [...conteo.entries()]
    .map(([motivo, total]) => ({
      motivo,
      etiqueta: etiquetaMotivo(motivo),
      total,
      pct: pctSobre(total, llamadas.length),
    }))
    .sort((a, b) => b.total - a.total || a.etiqueta.localeCompare(b.etiqueta, 'es'));
}

// --- Derivaciones (a quién se escaló) ---

export interface Derivacion {
  destino: 'operaciones' | 'comercial' | 'otros';
  etiqueta: string;
  total: number;
}

// Cuenta SOLO escaladas; 3 destinos siempre, y el reparto NO se escribe aquí: sale de
// `destinoEscalado`, que es el mismo que usan la columna de la tabla, el Excel y la línea
// de tiempo del detalle. Estaba copiado, y la copia es como una de las tres superficies
// acabó nombrando distinto el tercer cubo (vuelta 15 del gate). Las claves y las etiquetas
// de esta función sí se quedan: son su contrato, y `otros` es el nombre del CUBO.
export function derivaciones(llamadas: LlamadaFeedRow[]): Derivacion[] {
  const escaladas = llamadas.filter((l) => estadoVisual(l) === 'escalada');
  const cuenta = (destino: DestinoEscalado) =>
    escaladas.filter((l) => destinoEscalado(l) === destino).length;
  return [
    { destino: 'operaciones', etiqueta: 'Operaciones', total: cuenta('operaciones') },
    { destino: 'comercial', etiqueta: 'Comercial', total: cuenta('comercial') },
    { destino: 'otros', etiqueta: 'Otros escalados', total: cuenta('oficina') },
  ];
}

// --- Delta del KPI "Llamadas atendidas" vs periodo previo ---

export interface DeltaPeriodo {
  pct: number | null;
  direccion: 'sube' | 'baja' | 'igual' | null;
}

export function deltaLlamadas(totalActual: number, totalPrevio: number): DeltaPeriodo {
  if (totalPrevio === 0) return { pct: null, direccion: null };
  const pct = Math.round(((totalActual - totalPrevio) / totalPrevio) * 100);
  return { pct, direccion: pct > 0 ? 'sube' : pct < 0 ? 'baja' : 'igual' };
}

// --- Clientes que más llaman (change 003) ---

export interface ClienteAgregado {
  clave: string; // id_cliente, SIN_CLIENTE o CLAVE_RESTO
  etiqueta: string;
  total: number;
  pct: number;
  tipo: 'cliente' | 'sin_cliente' | 'resto';
  clientes?: number; // solo en «Resto»: cuántos clientes agrupa
}

export const CLAVE_RESTO = '__resto__';
const ETIQUETA_SIN_CLIENTE = 'Sin identificar';

const porTotalYNombre = (a: ClienteAgregado, b: ClienteAgregado): number =>
  b.total - a.total || a.etiqueta.localeCompare(b.etiqueta, 'es');

// Se agrega aquí, sobre las mismas filas filtradas que el resto de gráficas (T1
// corregida del change 003), y no en la base: así las barras no pueden descuadrar
// con los KPIs ni con la tabla. Se cuenta por `id_cliente`, nunca por el nombre.
// «Sin identificar» es una barra propia y nunca entra en «Resto» (D4), y las N
// primeras más «Resto» suman el total del periodo.
export function clientesQueMasLlaman(llamadas: LlamadaFeedRow[], n = 8): ClienteAgregado[] {
  const total = llamadas.length;
  const porCliente = new Map<string, { etiqueta: string; total: number }>();
  let sinCliente = 0;
  for (const l of llamadas) {
    if (l.id_cliente === null) {
      sinCliente++;
      continue;
    }
    const ya = porCliente.get(l.id_cliente);
    if (ya) ya.total++;
    else porCliente.set(l.id_cliente, { etiqueta: etiquetaCliente(l), total: 1 });
  }

  const clientes: ClienteAgregado[] = [...porCliente.entries()]
    .map(([clave, c]) => ({
      clave,
      etiqueta: c.etiqueta,
      total: c.total,
      pct: pctSobre(c.total, total),
      tipo: 'cliente' as const,
    }))
    .sort(porTotalYNombre);

  const barras = clientes.slice(0, n);
  if (sinCliente > 0) {
    barras.push({
      clave: SIN_CLIENTE,
      etiqueta: ETIQUETA_SIN_CLIENTE,
      total: sinCliente,
      pct: pctSobre(sinCliente, total),
      tipo: 'sin_cliente',
    });
  }
  barras.sort(porTotalYNombre);

  const resto = clientes.slice(n);
  if (resto.length > 0) {
    const suma = resto.reduce((s, c) => s + c.total, 0);
    barras.push({
      clave: CLAVE_RESTO,
      etiqueta: `Resto (${resto.length} ${resto.length === 1 ? 'cliente' : 'clientes'})`,
      total: suma,
      pct: pctSobre(suma, total),
      tipo: 'resto',
      clientes: resto.length,
    });
  }
  return barras;
}
